import { UsageData, UtilityFieldConfig } from "../../@types/oneButtonType";
import { billDetailFields, utilityCompanyFields } from "../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/UtilityDetailsModal";
import axios from "../../helpers/bearerAxios";
import { logger } from "../loggingService";

export function roundedBasedOn33Percent(value: number, isNewCalculationLogic: boolean = false): number {
    if (isNewCalculationLogic) {
        return Math.ceil(value);
    }
    const decimalPart = Number(value - Math.floor(value)).toFixed(2);
    return Number(decimalPart) > 0.33 ? Math.ceil(value) : Math.floor(value);
}

// Base URL for the OneButton Design API, retrieved from environment variables
const baseUrl = import.meta.env.VITE_ONEBUTTON_DESIGN_API_URL || '';
const designApiPath = baseUrl.includes('/api/Design') ? baseUrl : `${baseUrl.replace(/\/$/, '')}/api/Design`;
const rpaBoxUrl = import.meta.env.VITE_RPABOX_URL || '';
const designApiPathV2 = import.meta.env.VITE_ONEBUTTON_DESIGN_API_URL_V2 || '';
const designApiPathNonV2 = import.meta.env.VITE_ONEBUTTON_DESIGN_API_URL_NON_V2 || '';
const masterDataUrl = import.meta.env.VITE_ONEBUTTON_MASTER_API_URL || '';
const contractApiPath = import.meta.env.VITE_ONEBUTTON_CONTRACT_BASE_URL || '';

/** GetDesignImage response: design image metadata */
export interface GetDesignImageResponse {
    Id: string;
    Name: string;
}

/** SystemDesignLosses response (subset we use) */
export interface SystemDesignLossesResponse {
    SystemDesign_Losses_Id?: number;
    Opportunity_Id?: number;
    SimulationName?: string;
    ShadingEngineName?: string;
    InverterEfficiency?: number;
    DcToAcRatio?: number;
    WeatherDataset?: string;
    NameplateRating?: number;
    Mismatch?: number;
    Connections?: number;
    LightInducedDegradation?: number;
    Soiling?: number;
    Shading?: number;
    Availability?: number;
    Age?: number;
    Snow?: number;
    LossesSource?: string;
    [key: string]: unknown;
}

/** Roof object from RetrieveSystemDesign (for roof squares) */
export interface SystemDesignRoof {
    RoofSquare?: number;
    RoofSquares?: number;
    TotalRoofSquares?: number;
    LowSlope?: number;
    NormalSlope?: number;
    Low_Slope_Override?: number | null;
    Normal_Slope_Override?: number | null;
    RoofSquareOverride?: number | null;
    PricePerSqft?: number;
    MinRate?: number;
    ProposedRate?: number;
    ProductNumber?: string;
    ShingleColor?: string;
    [key: string]: unknown;
}

/** Sum roof squares from RetrieveSystemDesign.Roof (single object or array). */
export function getRoofSquaresFromSystemDesign(design: RetrieveSystemDesignResponse | null): number | null {
    const roof = design?.Roof;
    if (roof == null) return null;
    const sq = (r: SystemDesignRoof) => {
        const v = r?.RoofSquare ?? r?.TotalRoofSquares ?? r?.RoofSquares;
        return typeof v === 'number' && Number.isFinite(v) ? v : 0;
    };
    if (Array.isArray(roof)) {
        const total = roof.reduce((sum, r) => sum + sq(r), 0);
        return total > 0 ? total : null;
    }
    const v = sq(roof);
    return v > 0 ? v : null;
}

export interface BatterySystemDesign {
    Finance_Partner_Id?: string | number | null;
    Finance_Method_Name?: string | null;
    PricingMethod?: string | null;
    Price?: number | null;
    TermAndAPR?: string | null;
    Term?: number | null;
    APR?: number | null;
    Rebate?: number | null;
    DownPayment?: number | null;
    Deposit_Amount?: number | null;
    Tax?: number | null;
    DealerFee?: number | null;
    TotalCommissionableAmount?: number | null;
    grossSalesPrice?: number | null;
    Financed_Amount?: number | null;
    valid?: boolean;
    [key: string]: unknown;
}

export interface BatteryOnlySystemDesign {
    PriceTier?: string | null;
    BatteryAdder?: string[] | null;
    AdditionalCost?: number | null;
    totalAdderCost?: number | null;
    [key: string]: unknown;
}

/** RetrieveSystemDesign response (subset we use) */
export interface RetrieveSystemDesignResponse {
    Opportunity_Id?: string;
    SalesForce_Id?: string;
    OpportunityInterest?: any;
    SolarProductId?: number;
    BatteryProductId?: number;
    BatteryOnlyProductId?: number;
    RoofProductId?: number;
    RoofOnlyProductId?: number;
    DesignArray?: unknown[];
    Roof?: SystemDesignRoof | SystemDesignRoof[] | null;
    Battery?: BatterySystemDesign | null;
    BatteryOnly?: BatteryOnlySystemDesign | null;
    SolarUtility?: {
        UtilityCompanyName?: string;
        StateId?: string;
        [key: string]: unknown;
    } | null;
    InstallationAddress?: {
        StateId?: string;
        stateCode?: string;
        [key: string]: unknown;
    } | null;
    BatteryInformation?: {
        Manufacturer?: string;
        Name?: string;
        Quantity?: number | string;
        BatterySystemDesignId?: number;
        [key: string]: unknown;
    } | null;
    OpportunityProductsFinanceInfo?: Array<{
        ActiveFlag?: boolean;
        Product_Type_Name?: string;
        Manufacturer?: string;
        Name?: string;
        Quantity?: number | string;
        [key: string]: unknown;
    }>;
    Multiplier?: number | null;
    SystemSize?: number | null;
    SystemSizeDouble?: number | null;
    Production?: number | null;
    ProductionDouble?: number | null;
    IsDomestic?: boolean;
    FinanceMethod?: string;
    designJson?: string;
    isManual?: boolean;
    [key: string]: unknown;
}

// Get design history (Design_Json list)
export interface GetDesignHistoryItem {
    Design_Id: number;
    Design_Name: string;
    Design_Json: string;
    Active_Ind: boolean;
    User_Modified_Id: string;
    User_Modified_Timestamp: string;
    [key: string]: unknown;
}

/** Load Calculator item (Retrieve & Save Load Calculator APIs) */
export interface LoadCalculatorItem {
    OPLCId: number | null;
    LCTypeId: number;
    LCTypeName: string;
    MonthlyKWH: number;
    ProductLoadValue: number | null;
}

/** RetrievePriceQuote response (subset we use) */
export interface RetrievePriceQuoteResponse {
    Opportunity_Id?: number;
    SalesForce_Id?: string;
    InstallationState?: string;
    Solar?: Product | null;
    Battery?: Product | null;
    RoofQuote?: Product | null;
    RR?: Product | null;
    BatteryInformation?: {
        ManufacturerName?: string;
        BatteryModelName?: string;
        Quantity?: number;
        [key: string]: unknown;
    } | null;

    EPCCalc?: {
        UtilityName?: string;
        SystemSizeValue?: string;
        ProductionSizeValue?: string;
        [key: string]: unknown;
    } | null;

    QuoteVersion?: Array<{
        QuoteId?: number;
        ProductType?: string;
        FinancePartner?: string;
        PricingMethod?: string;
        PricingValue?: string;
        MonthlyPayment?: number;
        Year1Saving?: string;
        AnnualProduction?: string;
        QuoteRating?: string;
        Selected?: boolean;
        [key: string]: unknown;
    }>;
    [key: string]: unknown;
}

export interface Product {
    QuoteId?: number;
    FinancePartnerId?: number;
    FinanceMethodId?: number;
    Rate?: number | null;
    TermYears?: number | null;
    PricingMethod?: string | null;
    PricingValue?: number | string | null;
    DealerFee?: number;
    Total_Amount?: number | null;
    [key: string]: unknown;
}

const RetrieveUtilityInformation = async (opportunityId: any) => {
    try {
        const url = `${baseUrl}/RetrieveUtilityInformation?opportunityId=${opportunityId}`;
        const response = await axios.get(url);

        let updatedUtilityCompanyFieldList = [...utilityCompanyFields];
        updatedUtilityCompanyFieldList.forEach((field: UtilityFieldConfig) => {
            switch (field.field) {
                case "selectCompany":
                field.value = response.data.SolarUtility.UtilityCompany;
                break;
            case "selectCurrentRate":
                field.value = response.data.SolarUtility.CurrentRatePlanId;
                break;
            case "selectProposedRates":
                field.value = response.data.SolarUtility.PostInstallRatePlanId;
                break;  
            case "customerCharge":
                field.value = response.data.SolarUtility.CustomerCharge == 0 || response.data.SolarUtility.CustomerCharge == null ? '' : response.data.SolarUtility.CustomerCharge;
                break;

            case "outstandingBalance":
                field.value = response.data.SolarUtility.OutstandingBalance;
                break;
            case "selectUtilityIncrease":
                field.value = response.data.SolarUtility.EstAnnualUtilityIncrease;
                break;
            case "currentAllInUtilityRate":
                field.value = response.data.SolarUtility.CurrentAllInUtilityRate;
                break;
            case "utilityLIDRate":
                field.value = response.data.SolarUtility.UtilityLIDRate == true;
                break;
                default: break;
            }
        });

        let billDetails: any[] = [...billDetailFields];
        billDetails.forEach((field: any) => {
            switch (field.field) {
                case "shutoffNotice":
                    field.value = (response.data?.SolarUtility?.ShutoffNotice) ? true : false;
                    break;
                case "meterId":
                    field.value = response.data?.SolarMeter[0]?.MeterId;
                    break;
                case "currentBill":
                    field.value = response.data?.SolarUtility?.CurrentBill;
                    break;
                case "currentBillDate":
                    field.value = response.data?.SolarUtility?.CurrentBillDate;
                    break;
                case "proposedBill":
                    field.value = response.data?.SolarUtility?.ProposedBill;
                    break;
                case "proposedBillDate":
                    field.value = response.data?.SolarUtility?.ProposedBillDate;
                    break;
                case "accountNumber":
                    field.value = response.data?.SolarMeter[0]?.AccountNumber;
                    break;
                case "dateofBill":
                    field.value = response.data?.SolarMeter[0]?.BillDate;
                    break;
                case "utilityRate":
                    field.value = response.data?.SolarUtility?.Utility_Rate;
                    break;
                default: break;
            }
        });

        let utilityFile = null;
        if (response.data.SolarUtility.FilePath) {
            utilityFile = {
                FileName: response.data.SolarUtility.FileName,
                FilePath: response.data.SolarUtility.FilePath,
            }
        }
        const annualUsageType = response.data.SolarUtility.EnterAnnualUsage;
        
        let usageData: UsageData = { 
            dailyUsage: { key: 1, type: 'Daily', data: {} }, 
            monthlyUsage: { key: 2, type: 'Monthly', data: {} }, 
            yearlyUsage: { key: 3, type: 'Yearly', data: {} } 
        };
        
        // Daily Usage Cont
        usageData.dailyUsage.data = response.data.SolarUtility?.DailyUsage?.Data?.length 
            ? {
                AnnualUsage: response.data.SolarUtility.AnnualUsage,
                Data: response.data.SolarUtility?.DailyUsage?.Data,
              } 
            : {
                AnnualUsage: 0,
                Data: Array.from({ length: 12 }, (_, index) => ({
                    Month: new Date(0, index).toLocaleString('default', { month: 'long' }),
                    DailyUsage: 0,
                    MonthUsage: 0
                })),
            }
            
        // Monthly Usage Cont
        usageData.monthlyUsage.data = {
            AnnualUsage: 0,
            AnnualAmount: 0,
            Data: Array.from({ length: 12 }, (_, index) => ({
                Month: new Date(0, index).toLocaleString('default', { month: 'short'}),
                MonthUsage: response.data?.SolarUtility[`${new Date(0, index).toLocaleString('default', { month: 'long'})}`],
                Amount: response.data?.SolarUtility[`${new Date(0, index).toLocaleString('default', { month: 'long'})}Monthly`],
            })),
        };

        // Yearly Usage Cont
        usageData.yearlyUsage.data = response.data.SolarUtility?.AnnualUsage ? {
            AnnualUsage: response.data.SolarUtility.AnnualUsage,
            ElectricBillMonth: response.data.SolarUtility.ElectricBillMonth,
            avgDailyUsage: Number(response.data.SolarUtility.AnnualUsage / 365).toFixed(2), //response.data.SolarUtility.AnnualAmount / 365,
        } : {
            AnnualUsage: 0,
            ElectricBillMonth: "",
            avgDailyUsage: 0
        }  
        return { utilityDetails: response.data, utilityFieldList: updatedUtilityCompanyFieldList, billDetails, utilityFile, annualUsageType, usageData };
    } catch (error) {
        logger.error(error);
        throw error;
    }
};


const UploadUtilityFile = async (file: File, opportunityId: string, onUploadProgress: (progress: number) => void) => {
    try {
        const url = `${baseUrl}/UploadUtilityBill`;
        const SalesForce_Id = sessionStorage.getItem('SalesForceId') ?? '';
        const formData = new FormData();
        formData.append('UploadBill', file);
        formData.append('SalesForce_Id', SalesForce_Id);
        formData.append('opportunityId', opportunityId);
        // formData.append('fileType', fileType);
        const response = await axios.post(url, formData, {
            onUploadProgress: (progressEvent: any) => {
                const percentCompleted = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total
                );
                onUploadProgress(percentCompleted);
            }
        });
        return response.data;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};
/**
 * Get design image metadata (Id, Name). Image URL can be constructed as
 * GetDesignImage?opportunityId=X (or with &id=Id for the actual image).
 */
const GetDesignImage = async (opportunityId: string): Promise<GetDesignImageResponse | null> => {
    try {
        const url = `${designApiPath}/GetDesignImage?opportunityId=${encodeURIComponent(opportunityId)}`;
        const response = await axios.get<GetDesignImageResponse>(url);
        const data = response?.data ?? response;
        return data && typeof data.Id !== 'undefined' ? data : null;
    } catch (error) {
        logger.error(error);
        return null;
    }
};

/** Build URL for design image (for use as img src). Uses opportunityId and optional image Id from GetDesignImage. */
export function getDesignImageUrl(opportunityId: string, imageId?: string | null): string {
    const params = new URLSearchParams({ opportunityId });
    if (imageId) params.set('id', imageId);
    return `${designApiPath}/GetDesignImage?${params.toString()}`;
}

const SystemDesignLosses = async (opportunityId: string): Promise<SystemDesignLossesResponse | null> => {
    try {
        const url = `${designApiPath}/SystemDesignLosses?opportunityId=${encodeURIComponent(opportunityId)}`;
        const response = await axios.get<SystemDesignLossesResponse>(url);
        const data = response?.data ?? response;
        return data && typeof data === 'object' ? data : null;
    } catch (error) {
        logger.error(error);
        return null;
    }
};

const RetrieveSystemDesign = async (opportunityId: string): Promise<RetrieveSystemDesignResponse | null> => {
    try {
        const url = `${designApiPathV2}/RetrieveSystemDesign?opportunityId=${encodeURIComponent(opportunityId)}`;
        const response = await axios.get<RetrieveSystemDesignResponse>(url);
        const data = response?.data ?? response;
        return data && typeof data === 'object' ? data : null;
    } catch (error) {
        logger.error(error);
        return null;
    }
};


const RetrieveLoadCalculator = async (opportunityId: number): Promise<any> => {
    try {
        const url = `${designApiPath}/RetrieveLoadCalculator?OpportunityId=${encodeURIComponent(opportunityId)}`;
        const response = await axios.get<any>(url);
        return response?.data ?? response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

const SaveProductConfigLoadCalculator = async (payload: any): Promise<any> => {
    try {
        const url = `${designApiPath}/SaveProductConfigLoadCalculator`;
        const response = await axios.post<any>(url, payload);
        return response?.data ?? response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};


const GetDesignHistory = async (opportunityId: string | number): Promise<GetDesignHistoryItem[] | null> => {
    try {
        const url = `${designApiPath}/GetDesignHistory?opportunityId=${encodeURIComponent(opportunityId)}`;
        const response = await axios.get<{ Data: GetDesignHistoryItem[] }>(url);
        const data = response?.data?.Data;
        return Array.isArray(data) ? data : null;
    } catch (error) {
        logger.error(error);
        return null;
    }
};


const SaveSystemDesign = async (data: any) => {
    try {
        const url = `${designApiPathNonV2}/SaveSystemDesign`;
        const response = await axios.post<any>(url, data);
        return response?.data ?? response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

const RetrieveRoofProposalDetail = async (opportunityId: string): Promise<any> => {
    try {
        const url = `${designApiPath}/RetrieveRoofProposalDetail?opportunityId=${encodeURIComponent(opportunityId)}`;
        const response = await axios.get<any>(url);
        return response?.data ?? response;
    } catch (error) {
        logger.error(error);
        return null;
    }
};

const SaveRoofProposalDetail = async (payload: any): Promise<any> => {
    try {
        const url = `${designApiPath}/SaveRoofProposalDetail`;
        const response = await axios.post<any>(url, payload);
        return response?.data ?? response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

const SaveProductConfigUtility = async (data: any) => {
    try {
        const url = `${baseUrl}/SaveProductConfigUtility`;
        const response = await axios.post<any>(url, data);
        return response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

/**
 * Trinity `utility-info.component` `lightReachVoidQuote` — Design API voids the LightReach solar quote
 * after the user confirms the change-order utility save dialog (same base URL family as `RetrieveUtilityInformation`).
 */
const lightReachVoidQuote = async (opportunityId: string | number, LeadId: string | number) => {
    try {
        const url = `${contractApiPath}/api/Contract/LightReachVoidQuote?opportunityId=${encodeURIComponent(String(opportunityId))}&LeadId=${encodeURIComponent(String(LeadId))}`;
        const response = await axios.post<unknown>(url, null);
        return response?.data ?? response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

const GetEquipmentMasterList = async (financePartnerId?: any): Promise<any> => {
    try {
        const url = `${masterDataUrl}/GetEquipmentMasterList?financePartnerId=${financePartnerId}`;
        const response = await axios.get<any>(url);
        return response?.data ?? response;
    } catch (error) {
        logger.error(error);
        return null;
    }
};

const getBatteryModel = async (batteryManufacturerName: string, financePartnerId: any) => {
    try {
        const url = `${masterDataUrl}/GetBatteryModal?batteryManufacturerName=${batteryManufacturerName}&financePartnerId=${financePartnerId}`;
        const response = await axios.get(url);
        return response;
    } catch (error) {
        logger.error(error);
        throw error;
    }
};

const getRoofcontractsigned = async (opportunityId: any) => {
    try {
        const url = `${contractApiPath}/api/Contract/getRoofcontractsigned?opportunityId=${opportunityId}`;
        const response = await axios.get(url);
        return response?.data ?? response;
    } catch (error) {
        logger.error(error);
        return false;
    }
};

const RetrieveBuildingWithRoof = async (salesforceId: string) => {
    try {
        const oneDrawUrl = import.meta.env.VITE_ONEDRAW_API_URL;
        const url = `${oneDrawUrl}RetrieveBuildingWithRoof?Salesforce_Id=${salesforceId}`;
        const response = await axios.get<any>(url);
        return response?.data ?? response;
    } catch (error) {
        logger.error(error);
        return null;
    }
};

const GetRoofAreasByOpportunityId = async (salesforceId: string) => {
    try {
        const authUrl = import.meta.env.VITE_ONEBUTTON_AUTH_URL;
        const url = `${authUrl}DesignProcessRoofArea/GetRoofAreasByOpportunityId/${salesforceId}`;
        const response = await axios.get<any>(url);
        return response?.data ?? response;
    } catch (error) {
        logger.error(error);
        return null;
    }
};

/**
 * Trinity `GenerateQuoteComponent.CheckNewDesignExistAPI` — GET Contract before quote when Lightreach
 * change-order row exists but utility/system-design sync popup does not apply.
 * Returns a user-facing message to block quote generation, or `null` when OK to proceed.
 */
const checkNewDesignExist = async (opportunityId: string | number): Promise<any | null> => {
    try {
        const url = `${designApiPath}/CheckNewDesignExist?opportunityId=${encodeURIComponent(String(opportunityId))}`;
        const response = await axios.get(url);
        return response;
    } catch (error: any) {
        logger.error(error);
        throw error;
    }
};

const designServices = {
    RetrieveUtilityInformation,
    GetDesignImage,
    getDesignImageUrl,
    SystemDesignLosses,
    RetrieveSystemDesign,
    RetrieveLoadCalculator,
    SaveProductConfigLoadCalculator,
    GetDesignHistory,
    UploadUtilityFile,
    SaveProductConfigUtility,
    lightReachVoidQuote,
    GetEquipmentMasterList,
    getBatteryModel,
    getRoofcontractsigned,
    getRoofSquaresFromSystemDesign,
    roundedBasedOn33Percent,
    SaveSystemDesign,
    RetrieveRoofProposalDetail,
    SaveRoofProposalDetail,
    RetrieveBuildingWithRoof,
    checkNewDesignExist,
    GetRoofAreasByOpportunityId
};

export default designServices;